import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, C, GRAD, Loader, PrimaryButton, Avatar } from "@/components/district/kit";
import {
  Buildings, ArrowRight, UsersThree, PaperPlaneTilt, Plus,
  MapPinLine, CalendarCheck, ShieldCheck, UserCircle, X, Gear,
  ArrowsClockwise, Sparkle, Copy,
} from "@phosphor-icons/react";

const LOGO = `${process.env.PUBLIC_URL || ""}/rotary-reverse.png`;
const CARD_TINTS = ["#7C4DFF", "#5B34D6", "#A24BE0"]; // violet · deep indigo · plum (on-theme)
const field = "min-h-[52px] w-full rounded-xl border bg-white px-4 text-[16px] outline-none focus:border-[#7C4DFF]";

const activeRy = (d) => (d.rotary_years || []).find((y) => y.active) || (d.rotary_years || [])[0];
const adminName = (d) => d.district_admin?.display_name || d.district_admin?.name;

function HeroStat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/15"><Icon size={17} weight="bold" /></span>
      <div className="leading-none">
        <p className="text-[30px] font-bold">{value}</p>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-white/70">{label}</p>
      </div>
    </div>
  );
}

function Hero({ totals }) {
  return (
    <div className="relative overflow-hidden rounded-[32px] px-8 py-8 text-white md:px-10 md:py-9" style={{ background: GRAD.violet }}>
      <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
      <div className="pointer-events-none absolute -bottom-28 right-24 h-56 w-56 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
      <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-xl">
          <img src={LOGO} alt="Rotary" className="mb-5 h-8 w-auto object-contain" />
          <h1 className="text-[28px] font-bold leading-tight tracking-tight md:text-[36px]">Welcome</h1>
          <p className="mt-2.5 max-w-md text-[15px] font-medium leading-relaxed text-white/85 md:text-[16px]">
            You are overseeing {totals.districts} Rotary district{totals.districts === 1 ? "" : "s"}. Set up a year,
            enter a district, or manage the people who run it.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:w-auto" data-testid="hero-stats">
          <HeroStat icon={Buildings} value={totals.districts} label="Districts" />
          <HeroStat icon={MapPinLine} value={totals.clubs} label="Clubs" />
          <HeroStat icon={CalendarCheck} value={totals.withYear} label="Active years" />
          <HeroStat icon={ShieldCheck} value={totals.withAdmin} label="Admins set" />
        </div>
      </div>
    </div>
  );
}

function DistrictCard({ d, tint, busy, onGo }) {
  const ry = activeRy(d);
  const admin = adminName(d);
  return (
    <div data-testid={`district-card-${d.district_id}`}
      onClick={() => ry && !busy && onGo(d, "edit", "/")}
      className={`relative flex flex-col overflow-hidden rounded-[22px] p-4 text-white transition-transform duration-200 hover:-translate-y-1 sm:aspect-square ${ry ? "cursor-pointer" : ""}`}
      style={{ background: tint, boxShadow: "0 8px 22px rgba(0,0,0,0.08)" }}>
      <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full" style={{ background: "rgba(255,255,255,0.10)" }} />
      <img src={LOGO} alt="Rotary" className="relative mb-auto h-5 w-auto self-start object-contain object-left opacity-95" />
      <div className="relative mt-4">
        <p className="text-[16px] font-bold leading-tight tracking-tight">{d.name || `Rotary District ${d.rotary_district_no}`}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-medium text-white/90">
          <span>{d.clubs_count ?? 0} clubs</span>
          <span className="h-1 w-1 rounded-full bg-white/50" />
          <span>{ry ? `RY ${ry.label}` : "No year"}</span>
        </div>
        <p className="mt-0.5 truncate text-[10px] font-medium text-white/70">{admin ? `Admin · ${admin}` : "No admin assigned"}</p>
        <div className="mt-3">
          {ry ? (
            <button data-testid={`enter-${d.district_id}`} disabled={busy} onClick={() => onGo(d, "edit", "/")}
              className="flex w-full items-center justify-center gap-1.5 rounded-full bg-white px-3 py-2.5 text-[13px] font-bold transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ color: tint }}>
              Enter district <ArrowRight size={13} weight="bold" />
            </button>
          ) : (
            <div data-testid={`no-year-${d.district_id}`}
              className="rounded-full bg-white/15 px-3 py-2.5 text-center text-[11.5px] font-semibold text-white/85 backdrop-blur-sm">
              Set up a Rotary Year first
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RightRail({ districts, onManage }) {
  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[17px] font-bold tracking-tight" style={{ color: C.ink }}>District admins</h2>
        <button data-testid="manage-users-btn" onClick={() => onManage(null)}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-bold transition-colors hover:bg-[#F4F5FB]"
          style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line }}>
          <Gear size={14} weight="bold" /> Manage users
        </button>
      </div>
      <div className="space-y-1">
        {districts.map((d) => {
          const admin = adminName(d);
          return (
            <button key={d.district_id} data-testid={`admin-row-${d.district_id}`} onClick={() => onManage(d.district_id)}
              className="group flex w-full items-center gap-3.5 rounded-2xl px-1 py-2.5 text-left transition-colors hover:bg-[#F7FAFF]">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-[14px] font-bold"
                style={{ background: admin ? C.indigoSoft : C.bg, color: admin ? C.indigo : C.disabled }}>
                {admin ? admin.slice(0, 1).toUpperCase() : <UserCircle size={20} weight="bold" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold" style={{ color: C.ink }}>{d.name || `District ${d.rotary_district_no}`}</span>
                <span className="block truncate text-[13px] font-medium" style={{ color: admin ? C.sub : C.cranberry }}>{admin || "Assign an admin"}</span>
              </span>
              <ArrowRight size={15} weight="bold" className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100" style={{ color: C.sub }} />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function ManageUsersDrawer({ districts, initialDistrict, onClose }) {
  const ROLES = [
    { label: "District Governor", scope: "district", role: "District Governor" },
    { label: "District Secretary", scope: "district", role: "District Secretary" },
    { label: "District Admin", scope: "district", role: "District Admin" },
    { label: "Assistant Governor", scope: "district", role: "Assistant Governor" },
    { label: "Zone Governor", scope: "district", role: "Zone Governor" },
    { label: "Club President", scope: "club", role: "Club President" },
    { label: "Club Secretary", scope: "club", role: "Club Secretary" },
  ];
  const [did, setDid] = useState(initialDistrict || "");
  const [ready, setReady] = useState(false);
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState({ display_name: "", email: "", phone: "", roleIdx: 0, club_id: "" });
  const [busy, setBusy] = useState(false);
  const pickDistrict = async (id) => {
    setDid(id); setReady(false); setUsers([]);
    if (!id) return;
    const d = (districts || []).find((x) => x.district_id === id);
    const ry = activeRy(d || {});
    if (!ry) { toast.error("District has no active Rotary Year"); return; }
    try {
      await api.post("/support/context", { district_id: id, rotary_year_id: ry.rotary_year_id, mode: "edit" });
      const [u, dd] = await Promise.all([api.get("/support/users"), api.get(`/support/districts/${id}`)]);
      setUsers(u.data.users || []); setClubs(dd.data.clubs || []); setReady(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open district"); }
  };
  useEffect(() => { if (initialDistrict) pickDistrict(initialDistrict); }, []); // eslint-disable-line
  const invite = async (uid) => {
    try { const r = await api.post(`/access/accounts/${uid}/invite`); toast.success(r.data.delivery === "sent" ? "Invitation emailed" : "Invitation created — email could not be delivered"); }
    catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };
  const create = async () => {
    const r = ROLES[form.roleIdx];
    if (!form.display_name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (r.scope === "club" && !form.club_id) { toast.error("Select a club for this role"); return; }
    setBusy(true);
    try {
      const assignment = r.scope === "club" ? { scope: "club", role: r.role, club_id: form.club_id } : { scope: "district", role: r.role };
      const res = await api.post("/access/accounts", { display_name: form.display_name, email: form.email, phone: form.phone, assignment });
      await api.post(`/access/accounts/${res.data.user_id}/invite`).catch(() => {});
      toast.success("User created and invitation emailed");
      setForm({ display_name: "", email: "", phone: "", roleIdx: 0, club_id: "" });
      const u = await api.get("/support/users"); setUsers(u.data.users || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };
  const role = ROLES[form.roleIdx];
  const Label = ({ children }) => <label className="mb-1.5 block text-[14px] font-medium" style={{ color: C.ink }}>{children}</label>;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="manage-users-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-[#F8F9FA] shadow-2xl sm:rounded-l-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white/90 px-6 py-4 backdrop-blur" style={{ borderColor: C.line }}>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>Manage users</h2>
          <button data-testid="drawer-close" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F4F5FB]"><X size={18} weight="bold" style={{ color: C.sub }} /></button>
        </div>
        <div className="space-y-6 p-6">
          <div>
            <Overline className="mb-2.5 ml-0.5">District</Overline>
            <select data-testid="um-district" value={did} onChange={(e) => pickDistrict(e.target.value)} className={field} style={{ borderColor: C.line }}>
              <option value="">Select a district to manage users…</option>
              {(districts || []).map((d) => <option key={d.district_id} value={d.district_id}>{d.name || `District ${d.rotary_district_no}`}</option>)}
            </select>
          </div>
          {ready && (
            <>
              <Card>
                <h3 className="text-[16px] font-bold tracking-tight" style={{ color: C.ink }}>Create a user</h3>
                <div className="mt-5 space-y-4">
                  <div><Label>Full name</Label><input data-testid="um-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={field} style={{ borderColor: C.line }} /></div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label>Email</Label><input data-testid="um-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} style={{ borderColor: C.line }} /></div>
                    <div><Label>Phone</Label><input data-testid="um-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} style={{ borderColor: C.line }} /></div>
                  </div>
                  <div><Label>Role</Label>
                    <select data-testid="um-role" value={form.roleIdx} onChange={(e) => setForm({ ...form, roleIdx: +e.target.value, club_id: "" })} className={field} style={{ borderColor: C.line }}>
                      {ROLES.map((r, i) => <option key={r.label} value={i}>{r.label}</option>)}
                    </select>
                  </div>
                  {role.scope === "club" && (
                    <div><Label>Club</Label>
                      <select data-testid="um-club" value={form.club_id} onChange={(e) => setForm({ ...form, club_id: e.target.value })} className={field} style={{ borderColor: C.line }}>
                        <option value="">Select the club…</option>
                        {clubs.map((c) => <option key={c.club_id} value={c.club_id}>{c.club_name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <PrimaryButton data-testid="um-create" className="mt-5 w-full" onClick={create} disabled={busy}><Plus size={17} weight="bold" /> Create &amp; send invite</PrimaryButton>
              </Card>
              <div>
                <Overline className="mb-2.5 ml-0.5">Users</Overline>
                <Card pad={false} className="divide-y overflow-hidden" data-testid="um-users">
                  {users.length === 0 && <p className="px-5 py-5 text-[15px]" style={{ color: C.sub }}>No users yet.</p>}
                  {users.map((u) => (
                    <div key={u.user_id} className="flex items-center gap-3.5 px-5 py-3.5" data-testid={`um-user-${u.user_id}`}>
                      <Avatar icon={UsersThree} />
                      <span className="min-w-0 flex-1"><span className="block truncate text-[16px] font-medium" style={{ color: C.ink }}>{u.display_name || u.email}</span><span className="block text-[13px]" style={{ color: C.sub }}>{u.email} · {u.status}</span></span>
                      <button data-testid={`um-invite-${u.user_id}`} onClick={() => invite(u.user_id)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-4 text-[14px] font-medium" style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line }}><PaperPlaneTilt size={14} weight="bold" /> Invite</button>
                    </div>
                  ))}
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function DemoSandboxCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/support/demo/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);
  const reset = async () => {
    if (!window.confirm("Reset the Demo Sandbox (District 0000) to its original state? This affects only the demo — no real district is touched.")) return;
    setBusy(true);
    try { await api.post("/support/demo/reset", {}); toast.success("Demo Sandbox reset to its perfect state"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  return (
    <Card data-testid="demo-sandbox-card">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-xl" style={{ background: C.indigoSoft, color: C.indigo }}><Sparkle size={18} weight="fill" /></span>
        <div>
          <h2 className="text-[17px] font-bold tracking-tight" style={{ color: C.ink }}>Demo Sandbox</h2>
          <p className="text-[12.5px] font-medium" style={{ color: C.sub }}>District 0000 · for pitching to prospects</p>
        </div>
      </div>
      {status?.present ? (
        <div className="rounded-2xl border p-3.5" style={{ borderColor: C.line, background: "#FBFBFE" }}>
          <div className="flex items-center gap-4 text-[13px]" style={{ color: C.sub }}>
            <span><b style={{ color: C.ink }}>{status.clubs}</b> clubs</span>
            <span><b style={{ color: C.ink }}>{status.members}</b> members</span>
          </div>
          <div className="mt-2.5 space-y-1.5 text-[13px]">
            <button onClick={() => copy(status.login_email)} className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-3 py-2" style={{ boxShadow: "inset 0 0 0 1px " + C.line }} data-testid="demo-email">
              <span style={{ color: C.sub }}>Login</span><span className="font-semibold" style={{ color: C.ink }}>{status.login_email}</span><Copy size={13} style={{ color: C.sub }} />
            </button>
            <button onClick={() => copy(status.login_password)} className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-3 py-2" style={{ boxShadow: "inset 0 0 0 1px " + C.line }} data-testid="demo-password">
              <span style={{ color: C.sub }}>Password</span><span className="font-semibold" style={{ color: C.ink }}>{status.login_password}</span><Copy size={13} style={{ color: C.sub }} />
            </button>
          </div>
          <p className="mt-2 text-[11.5px]" style={{ color: C.sub }}>Read-only — prospects can explore every screen but cannot change data.</p>
        </div>
      ) : (
        <p className="text-[14px]" style={{ color: C.sub }}>Not built yet — click reset to create it.</p>
      )}
      <button data-testid="demo-reset-btn" onClick={reset} disabled={busy}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-[14px] font-bold text-white disabled:opacity-60" style={{ background: C.indigo }}>
        <ArrowsClockwise size={16} weight="bold" className={busy ? "animate-spin" : ""} /> Reset Demo Sandbox
      </button>
    </Card>
  );
}




export default function SupportHub() {
  const [districts, setDistricts] = useState(null);
  const [drawer, setDrawer] = useState(null); // null = closed; {district} when open
  useEffect(() => { api.get("/support/districts").then((r) => setDistricts(r.data.districts || [])).catch(() => setDistricts([])); }, []);
  const list = districts || [];
  const totals = {
    districts: list.length,
    clubs: list.reduce((s, d) => s + (d.clubs_count || 0), 0),
    withYear: list.filter((d) => activeRy(d)).length,
    withAdmin: list.filter((d) => adminName(d)).length,
  };

  const [busy, setBusy] = useState(false);
  const go = async (d, mode, dest) => {
    const ry = activeRy(d);
    if (!ry) { toast.error("No Rotary Year set up for this district yet"); return; }
    setBusy(true);
    try { await api.post("/support/context", { district_id: d.district_id, rotary_year_id: ry.rotary_year_id, mode }); window.location.href = dest; }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); }
  };

  return (
    <div className="mx-auto w-full max-w-[1360px]">
      <Hero totals={totals} />

      <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-8">
          <h2 className="mb-4 ml-1 text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>Your districts</h2>
          {!districts ? (
            <Loader label="Loading districts…" />
          ) : list.length === 0 ? (
            <Card><p className="py-8 text-center text-[15px]" style={{ color: C.sub }}>No districts yet.</p></Card>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="districts-grid">
              {list.map((d, i) => <DistrictCard key={d.district_id} d={d} tint={CARD_TINTS[i % CARD_TINTS.length]} busy={busy} onGo={go} />)}
            </div>
          )}
        </div>
        <div className="space-y-6 lg:col-span-4">
          {districts && <RightRail districts={list} onManage={(id) => setDrawer({ district: id })} />}
          <DemoSandboxCard />
        </div>
      </div>

      {drawer && <ManageUsersDrawer districts={list} initialDistrict={drawer.district} onClose={() => setDrawer(null)} />}
    </div>
  );
}
